/**
 * Contrato Web Push Gateway / Controller → UDV (notify vs send + multi-Edge).
 */

/**
 * @param {object|null|undefined} payload
 * @returns {boolean}
 */
export function isGatewayNotifyOnly(payload) {
  const data = Object.assign(
    {},
    (payload && payload.raw) || {},
    payload || {}
  )
  return (
    data.action === 'notify' ||
    data.type === 'study_notify' ||
    data.notify_only === true ||
    data.notify_only === '1' ||
    data.notify_only === 'true'
  )
}

/**
 * Normaliza base Edge (sin slash final).
 * @param {string|null|undefined} base
 * @returns {string}
 */
export function normalizeEdgeLanBase(base) {
  return String(base == null ? '' : base)
    .trim()
    .replace(/\/+$/, '')
}

/**
 * Probe corto de reachability LAN vía GET {base}/api/v1/health.
 * Soft-fail: cualquier error de red/CORS/timeout → false.
 * @param {string|null|undefined} edgeLanBase
 * @param {{ timeoutMs?: number, fetchImpl?: typeof fetch }} [opts]
 * @returns {Promise<boolean>}
 */
export async function probeEdgeLanHealth(edgeLanBase, opts = {}) {
  const base = normalizeEdgeLanBase(edgeLanBase)
  if (!base) return false
  const timeoutMs = opts.timeoutMs != null ? opts.timeoutMs : 800
  const fetchFn = opts.fetchImpl || fetch
  const url = `${base}/api/v1/health`
  let timer = null
  let abortCtrl = null
  try {
    const init = { method: 'GET', mode: 'cors', cache: 'no-store' }
    if (typeof AbortController !== 'undefined') {
      abortCtrl = new AbortController()
      init.signal = abortCtrl.signal
      timer = setTimeout(function () {
        try {
          abortCtrl.abort()
        } catch (e) {
          /* ignore */
        }
      }, timeoutMs)
    }
    const res = await fetchFn(url, init)
    return Boolean(res && res.ok)
  } catch (e) {
    return false
  } finally {
    if (timer) clearTimeout(timer)
  }
}

/**
 * Deriva base API del Edge desde edge_wado_url (quita sufijo /wado).
 * @param {string} edgeWadoUrl
 * @returns {string}
 */
export function resolveEdgeApiBase(edgeWadoUrl) {
  const raw = String(edgeWadoUrl == null ? '' : edgeWadoUrl).trim()
  if (!raw) {
    throw new Error('edge_wado_url vacío')
  }
  let url
  try {
    url = new URL(raw)
  } catch (e) {
    throw new Error('edge_wado_url inválida')
  }
  let path = url.pathname || ''
  path = path.replace(/\/+$/, '')
  if (/\/wado$/i.test(path)) {
    path = path.replace(/\/wado$/i, '')
  }
  path = path.replace(/\/+$/, '')
  url.pathname = path || '/'
  // base sin path vacío final: origin + path (sin slash final salvo root)
  const base = path ? `${url.origin}${path}` : url.origin
  return base.replace(/\/+$/, '')
}

/**
 * Arma payload de cold-start desde query.
 * @param {URLSearchParams} urlParams
 * @returns {object|null}
 */
export function pendingPushPayloadFromUrl(urlParams) {
  if (!urlParams || typeof urlParams.get !== 'function') return null
  const uid = urlParams.get('studyUID')
  if (!uid) return null
  const notifyFlag = urlParams.get('notify_only')
  const ingest = urlParams.get('ingest_complete')
  return {
    study_instance_uid: uid,
    action: urlParams.get('action') || undefined,
    type: urlParams.get('type') || undefined,
    notify_only:
      notifyFlag === '1' ||
      notifyFlag === 'true' ||
      urlParams.get('action') === 'notify' ||
      urlParams.get('type') === 'study_notify',
    patient_name: urlParams.get('patient_name') || undefined,
    ingest_complete: ingest === 'false' ? false : true,
    edge_wado_url: urlParams.get('edge_wado_url') || undefined,
    edge_lan_base: urlParams.get('edge_lan_base') || undefined,
    edge_id: urlParams.get('edge_id') || undefined,
    clinic_name: urlParams.get('clinic_name') || undefined,
    modality: urlParams.get('modality') || undefined,
    edge_version: urlParams.get('edge_version') || undefined,
    controller_version: urlParams.get('controller_version') || undefined,
  }
}
